import type { WorkTypeOption } from './useWorkTypeOptions';

/**
 * Template / approval "Work type" scope — contract section D.
 *
 * Every settings template scopes itself by Departments / Offices / Work type,
 * and each endpoint spells the work-type key differently. What does not vary is
 * the pair of rules these helpers exist to enforce in one place:
 *
 * 1. "All work types" is an empty array, the same convention as departments and
 *    offices — never the literal `'all'` the select carries.
 * 2. Flag ON reads and writes the work-type field only, flag OFF the legacy enum
 *    field only. Never both, and never an id under the legacy key.
 *
 * Written once because doing it per surface is how the first attempt shipped
 * "selection disappears after refresh": the flag-on select was initialised from
 * `employment_types`, which is empty for a custom-typed template.
 */

/** The select's "everything" sentinel; the API wants an empty array instead. */
export const ALL_WORK_TYPES = 'all';

const unwrapAll = (value: unknown): unknown[] => {
  if (!Array.isArray(value)) return [];
  return value[0] === ALL_WORK_TYPES ? [] : value;
};

/**
 * The work-type half of a scope payload, keyed for the surface being saved.
 *
 * @example
 * ...workTypeScopePayload(isWorkTypesEnabled, values.employment_types, {
 *   workTypeKey: 'entity_scope_ats_work_type_ids',
 *   legacyKey: 'entity_scope_employment_types',
 * })
 */
export const workTypeScopePayload = (
  isWorkTypesEnabled: boolean,
  value: unknown,
  keys: { workTypeKey: string; legacyKey: string }
): Record<string, unknown[]> => ({
  [isWorkTypesEnabled ? keys.workTypeKey : keys.legacyKey]: unwrapAll(value),
});

/**
 * Initial value for a scope select, read back from the API.
 *
 * Flag ON reads `ats_work_type_ids` and *only* that: a custom-typed template
 * has no `employment_types` to fall back on, so falling back would silently
 * blank the user's selection on every reload.
 */
export const workTypeScopeInitialValue = (
  isWorkTypesEnabled: boolean,
  scope: { ats_work_type_ids?: unknown; employment_types?: unknown } = {}
): unknown[] => {
  const selected = isWorkTypesEnabled
    ? scope?.ats_work_type_ids
    : scope?.employment_types;

  return Array.isArray(selected) && selected.length > 0
    ? selected
    : [ALL_WORK_TYPES];
};

/** Maps selected ids to their labels for a read-only scope display. */
export const workTypeScopeLabels = (
  isWorkTypesEnabled: boolean,
  scope: { ats_work_type_ids?: unknown; employment_types?: unknown } = {},
  options: WorkTypeOption[] = []
): string[] => {
  const selected = isWorkTypesEnabled
    ? scope?.ats_work_type_ids
    : scope?.employment_types;
  if (!Array.isArray(selected)) return [];

  return selected.map((value) => {
    const match = options.find((option) => option.value === value);
    return match?.label ?? String(value);
  });
};

/**
 * The work-type half of an entity-scope *lookup* — contract section C, the
 * "which template applies to a job like this?" queries.
 *
 * Separate from workTypeScopePayload because these send the job's own work
 * type rather than a user's selection, and the keys are fixed rather than
 * per-surface. Same guarantee: one key, never both.
 *
 * @example
 * const scope = {
 *   department_ids: [job.department.id],
 *   office_ids: job.office.map((o) => o.id),
 *   ...workTypeScopeParam(isWorkTypesEnabled, {
 *     atsWorkTypeIds: [workTypeId],
 *     employmentTypes: [job.employment_type],
 *   }),
 * };
 */
export const workTypeScopeParam = (
  isWorkTypesEnabled: boolean,
  {
    atsWorkTypeIds,
    employmentTypes,
  }: { atsWorkTypeIds?: unknown[]; employmentTypes?: unknown[] },
  /**
   * Key overrides. Most lookups take the plural `employment_types`, but
   * /ats_offer_templates slices on the singular `employment_type` — passing the
   * wrong one is silently unfiltered rather than an error.
   */
  keys: { workTypeKey?: string; legacyKey?: string } = {}
): Record<string, unknown[]> => {
  const workTypeKey = keys.workTypeKey ?? 'ats_work_type_ids';
  const legacyKey = keys.legacyKey ?? 'employment_types';
  return isWorkTypesEnabled
    ? { [workTypeKey]: (atsWorkTypeIds ?? []).filter(Boolean) }
    : { [legacyKey]: (employmentTypes ?? []).filter(Boolean) };
};

/** A job's work type id, wherever the API happened to put it. */
export const readJobWorkTypeId = (basicInfo: {
  ats_work_type?: { id?: number };
  ats_work_type_id?: number;
}): number | undefined =>
  basicInfo?.ats_work_type?.id ?? basicInfo?.ats_work_type_id;
